import { PosterDesign, PosterDesignProps } from '@/types/activation/poster';
import { brandGradient, darken } from '../lookbook/lookbookDesigns';
import { BrandMark, LookbookQR, PoweredByPill } from '../lookbook/LookbookPrimitives';

const INK = '#15233A';
const MUTED = '#5B6472';
const targetLabel = (brand: PosterDesignProps['brand']) =>
  brand.targetType === 'portal' ? 'help portal' : 'Genie';

// ============================================================================
// Poster designs — each is a full A4 page. Unlike the lookbook (a catalog of
// mockups), these ARE the print output: no caption chrome, edge-to-edge.
// ============================================================================

const StartHere = ({ brand }: PosterDesignProps) => (
  <div
    style={{
      width: '100%', height: '100%', background: brandGradient(brand, 150), position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '90px 70px',
    }}
  >
    <div style={{ position: 'absolute', top: -140, left: -140, width: 420, height: 420, borderRadius: '50%', background: 'rgba(255,255,255,.08)' }} />
    <BrandMark brand={brand} size={72} radius={18} />
    <div style={{ color: '#fff', fontWeight: 800, fontSize: 72, lineHeight: 1.02, textAlign: 'center', margin: '40px 0 16px' }}>
      Start<br />here.
    </div>
    <div style={{ color: 'rgba(255,255,255,.85)', fontWeight: 600, fontSize: 24, textAlign: 'center', marginBottom: 56 }}>
      Scan to open {brand.name} on your phone.
    </div>
    <LookbookQR brand={brand} size={260} />
    <div style={{ marginTop: 'auto' }}><PoweredByPill scale={1.3} /></div>
  </div>
);

const MeetYourGenie = ({ brand }: PosterDesignProps) => (
  <div style={{ width: '100%', height: '100%', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 64px' }}>
    <BrandMark brand={brand} size={96} radius={22} />
    <div style={{ color: INK, fontWeight: 800, fontSize: 54, textAlign: 'center', margin: '30px 0 10px' }}>Meet {brand.name}.</div>
    <div style={{ color: brand.primary, fontWeight: 700, fontSize: 20, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 40 }}>
      Your new {targetLabel(brand)}
    </div>
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 48 }}>
      {['A simple new way to ask', 'Open it directly from this QR', 'Try it from your phone'].map((line) => (
        <div key={line} style={{ display: 'flex', alignItems: 'center', gap: 14, background: `${brand.primary}0C`, borderRadius: 14, padding: '18px 22px' }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: brand.primary, flexShrink: 0 }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: INK }}>{line}</span>
        </div>
      ))}
    </div>
    <LookbookQR brand={brand} size={230} />
    <div style={{ color: MUTED, fontSize: 18, fontWeight: 600, marginTop: 20 }}>Scan to try it yourself</div>
    <div style={{ marginTop: 'auto' }}><PoweredByPill scale={1.2} /></div>
  </div>
);

const NowAnswering = ({ brand }: PosterDesignProps) => (
  <div
    style={{
      width: '100%', height: '100%', background: INK, position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '90px 64px',
    }}
  >
    <div style={{ position: 'absolute', bottom: -160, right: -160, width: 460, height: 460, borderRadius: '50%', background: `${brand.primary}22` }} />
    <div style={{ padding: '10px 24px', borderRadius: 999, border: `2px solid ${brand.secondary}`, color: brand.secondary, fontWeight: 700, fontSize: 18, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
      Now live
    </div>
    <div style={{ color: '#fff', fontWeight: 800, fontSize: 66, lineHeight: 1.05, textAlign: 'center', margin: '36px 0 16px' }}>
      A new way<br />to ask us.
    </div>
    <div style={{ color: 'rgba(255,255,255,.7)', fontSize: 22, fontWeight: 500, textAlign: 'center', marginBottom: 56 }}>
      {brand.name} is one scan away.
    </div>
    <LookbookQR brand={brand} size={250} />
    <div style={{ marginTop: 'auto' }}><PoweredByPill scale={1.2} /></div>
  </div>
);

const HOW_IT_WORKS_STEPS: [string, string, string][] = [
  ['1', 'Scan the code', 'Point your camera at the QR below.'],
  ['2', 'Ask your question', 'Type or talk — whatever’s easiest.'],
  ['3', 'Continue on your phone', 'Open it and take it from there.'],
];

const HowItWorks = ({ brand }: PosterDesignProps) => (
  <div style={{ width: '100%', height: '100%', background: '#F4F7FB', display: 'flex', flexDirection: 'column', padding: '80px 64px' }}>
    <BrandMark brand={brand} size={60} radius={16} />
    <div style={{ color: INK, fontWeight: 800, fontSize: 50, margin: '28px 0 44px', lineHeight: 1.05 }}>Three steps<br />to get started.</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 48 }}>
      {HOW_IT_WORKS_STEPS.map(([n, h, b]) => (
        <div key={n} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ width: 56, height: 56, flexShrink: 0, borderRadius: '50%', background: brand.primary, color: '#fff', fontWeight: 800, fontSize: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {n}
          </div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: INK }}>{h}</div>
            <div style={{ fontSize: 18, color: MUTED, marginTop: 4 }}>{b}</div>
          </div>
        </div>
      ))}
    </div>
    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <LookbookQR brand={brand} size={220} />
      <PoweredByPill scale={1.2} />
    </div>
  </div>
);

const TeamAnnouncement = ({ brand }: PosterDesignProps) => (
  <div
    style={{
      width: '100%', height: '100%', background: brandGradient(brand, 200), position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '90px 64px',
    }}
  >
    <div style={{ position: 'absolute', top: -100, right: -100, width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,.10)' }} />
    <div style={{ color: '#fff', fontWeight: 700, fontSize: 18, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85 }}>Team update</div>
    <div style={{ color: '#fff', fontWeight: 800, fontSize: 58, lineHeight: 1.05, textAlign: 'center', margin: '22px 0 16px' }}>
      Say hello to<br />{brand.name}.
    </div>
    <div style={{ color: 'rgba(255,255,255,.85)', fontSize: 22, fontWeight: 500, textAlign: 'center', maxWidth: '80%', marginBottom: 56 }}>
      Our new {targetLabel(brand)} is live — scan below and give {brand.name} a try.
    </div>
    <LookbookQR brand={brand} size={240} />
    <div style={{ marginTop: 'auto' }}><PoweredByPill scale={1.2} /></div>
  </div>
);

const BigScan = ({ brand }: PosterDesignProps) => (
  <div
    style={{
      width: '100%', height: '100%', background: '#fff', position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 64px',
    }}
  >
    <div style={{ position: 'absolute', inset: 0, border: `18px solid ${brand.primary}` }} />
    <div style={{ color: brand.primary, fontWeight: 800, fontSize: 30, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 48 }}>
      Scan me
    </div>
    <LookbookQR brand={brand} size={340} />
    <div style={{ color: INK, fontWeight: 700, fontSize: 30, marginTop: 52, textAlign: 'center', maxWidth: '85%' }}>
      Open {brand.name} on your phone.
    </div>
    <div style={{ marginTop: 'auto', paddingTop: 40 }}><PoweredByPill scale={1.2} /></div>
  </div>
);

const ZeroWait = ({ brand }: PosterDesignProps) => (
  <div
    style={{
      width: '100%', height: '100%', background: brandGradient(brand, 165), position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '88px 64px',
    }}
  >
    <div style={{ position: 'absolute', bottom: -160, left: -160, width: 420, height: 420, borderRadius: '50%', background: 'rgba(255,255,255,.10)' }} />
    <BrandMark brand={brand} size={64} radius={16} />
    <div style={{ color: '#fff', fontWeight: 800, fontSize: 100, lineHeight: 1, marginTop: 40 }}>One</div>
    <div style={{ color: '#fff', fontWeight: 800, fontSize: 42, marginTop: 6, letterSpacing: '0.01em' }}>quick scan.</div>
    <div style={{ color: 'rgba(255,255,255,.8)', fontSize: 19, marginTop: 20, textAlign: 'center', maxWidth: '78%', fontWeight: 500 }}>
      Open the {targetLabel(brand)} directly from this QR code.
    </div>
    <div style={{ marginTop: 44 }}><LookbookQR brand={brand} size={210} /></div>
    <div style={{ marginTop: 'auto' }}><PoweredByPill scale={1.2} /></div>
  </div>
);

const SAMPLE_QUESTIONS = ['What can you help me with?', 'How do I get started?', 'Where should I begin?'];

const CommonQuestions = ({ brand }: PosterDesignProps) => (
  <div style={{ width: '100%', height: '100%', background: '#fff', display: 'flex', flexDirection: 'column', padding: '76px 60px' }}>
    <BrandMark brand={brand} size={56} radius={14} />
    <div style={{ color: INK, fontWeight: 800, fontSize: 44, margin: '26px 0 8px', lineHeight: 1.08 }}>
      Ask us like<br />you'd ask a friend.
    </div>
    <div style={{ color: brand.primary, fontWeight: 700, fontSize: 16, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 36 }}>
      Real examples
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 44 }}>
      {SAMPLE_QUESTIONS.map((q) => (
        <div
          key={q}
          style={{
            alignSelf: 'flex-start', maxWidth: '92%', background: `${brand.primary}0E`, border: `1.5px solid ${brand.primary}33`,
            borderRadius: '20px 20px 20px 4px', padding: '16px 22px', fontSize: 19, fontWeight: 600, color: INK,
          }}
        >
          &ldquo;{q}&rdquo;
        </div>
      ))}
    </div>
    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
      <LookbookQR brand={brand} size={200} />
      <div style={{ color: MUTED, fontSize: 16, fontWeight: 600 }}>Scan and ask your own.</div>
      <PoweredByPill scale={1.1} />
    </div>
  </div>
);

const TakeItWithYou = ({ brand }: PosterDesignProps) => (
  <div
    style={{
      width: '100%', height: '100%', background: darken(brand.primary, 0.72), position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '88px 60px',
    }}
  >
    <div style={{ position: 'absolute', top: -140, left: -140, width: 400, height: 400, borderRadius: '50%', background: `${brand.primary}30` }} />
    <BrandMark brand={brand} size={64} radius={16} />
    <div style={{ color: '#fff', fontWeight: 800, fontSize: 50, textAlign: 'center', margin: '32px 0 14px', lineHeight: 1.08 }}>
      Take {brand.name}<br />with you.
    </div>
    <div style={{ color: 'rgba(255,255,255,.72)', fontSize: 19, textAlign: 'center', maxWidth: '80%', marginBottom: 34, fontWeight: 500 }}>
      Scan once to open this {targetLabel(brand)} on your phone.
    </div>
    <div style={{ marginTop: 24 }}><LookbookQR brand={brand} size={260} /></div>
    <div style={{ marginTop: 'auto' }}><PoweredByPill scale={1.2} /></div>
  </div>
);

const OldWayNewWay = ({ brand }: PosterDesignProps) => (
  <div style={{ width: '100%', height: '100%', background: '#F4F7FB', display: 'flex', flexDirection: 'column', padding: '70px 56px' }}>
    <BrandMark brand={brand} size={54} radius={14} />
    <div style={{ color: INK, fontWeight: 800, fontSize: 40, margin: '26px 0 40px', lineHeight: 1.1 }}>
      Put {brand.name} one scan away.
    </div>
    <div style={{ display: 'flex', gap: 20, flex: 1 }}>
      <div style={{ flex: 1, background: '#fff', border: '1.5px solid #E4E8F0', borderRadius: 20, padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', gap: 14 }}>
        <div style={{ color: MUTED, fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Step one</div>
        <div style={{ color: INK, fontWeight: 700, fontSize: 21 }}>See the code</div>
        <div style={{ color: MUTED, fontSize: 14.5 }}>Point your phone camera at the QR.</div>
      </div>
      <div style={{ flex: 1, background: brand.primary, borderRadius: 20, padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', gap: 14 }}>
        <div style={{ color: 'rgba(255,255,255,.85)', fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Step two</div>
        <LookbookQR brand={brand} size={128} />
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 21 }}>Open the {targetLabel(brand)}</div>
        <div style={{ color: 'rgba(255,255,255,.85)', fontSize: 14.5 }}>Continue with {brand.name} on your phone.</div>
      </div>
    </div>
    <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center' }}><PoweredByPill scale={1.1} /></div>
  </div>
);

// ============================================================================
// Registry
// ============================================================================

export const POSTER_DESIGNS: PosterDesign[] = [
  { id: 'ask-anything', name: 'Start Here', meta: 'Customer-facing · counter or window', Component: StartHere },
  { id: 'meet-your-genie', name: 'Meet Your Genie', meta: 'Introduction · reception or breakroom', Component: MeetYourGenie },
  { id: 'now-answering', name: 'Now Live', meta: 'Launch statement · storefront or lobby', Component: NowAnswering },
  { id: 'how-it-works', name: 'How It Works', meta: 'Step-by-step · counter or hallway', Component: HowItWorks },
  { id: 'team-announcement', name: 'Team Announcement', meta: 'Internal launch · staff room', Component: TeamAnnouncement },
  { id: 'big-scan', name: 'Big Scan', meta: 'High-visibility · storefront window or door', Component: BigScan },
  { id: 'zero-wait', name: 'One Quick Scan', meta: 'Direct action · queue area or waiting room', Component: ZeroWait },
  { id: 'common-questions', name: 'Common Questions', meta: 'Educational · counter or lobby', Component: CommonQuestions },
  { id: 'ask-any-language', name: 'Take It With You', meta: 'Mobile activation · customer-facing', Component: TakeItWithYou },
  { id: 'old-way-new-way', name: 'How to Open It', meta: 'Step-by-step · customer-facing', Component: OldWayNewWay },
];
